import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import Spinner from "../../../../components/spinner.components";
import { useGetCampaignByIdAdminHttp } from "../../../../hooks/http/admin/campaigns/get-campaign-by-id.admin.http";
import {
	CampaignMetricsExportColumn,
	useExportCsvCampaignMetricsAdminHttp,
} from "../../../../hooks/http/admin/campaigns/export-csv-campaign-metrics.admin.http";
import { useToast } from "../../../../hooks/use-toast";
import type { ICampaignMinimal } from "../../../../types/models/campaign.models.types";
import { PostStatus } from "../../../../types/models/post.models.types";
import { handleHttpError } from "../../../../utils/utils";

const STATUS_OPTIONS = [
	// Draft statuses
	{ label: "Draft Submitted", value: PostStatus.draft },
	{ label: "Draft Approved", value: PostStatus.draftApproved },
	{ label: "Draft Rejected", value: PostStatus.draftRejected },

	// Post statuses
	{ label: "Post Submitted", value: PostStatus.post },
	{ label: "Post Rejected", value: PostStatus.postRejected },
	{ label: "Post Approved / Live", value: PostStatus.postApproved },

	// Flagged statuses
	{ label: "Flagged", value: PostStatus.flagged },
	{ label: "Flagged Pending Review", value: PostStatus.flaggedReview },

	// Disqualified statuses
	{ label: "Disqualified", value: PostStatus.disqualify },
];

const COLUMN_OPTIONS = [
	{ label: "Page Name", value: CampaignMetricsExportColumn.pageName },
	{ label: "Profile Link", value: CampaignMetricsExportColumn.profileLink },
	{ label: "Post Link", value: CampaignMetricsExportColumn.postLink },
	{ label: "Date of Post", value: CampaignMetricsExportColumn.dateOfPost },
	{ label: "Views", value: CampaignMetricsExportColumn.views },
	{ label: "Likes", value: CampaignMetricsExportColumn.likes },
	{ label: "Comments", value: CampaignMetricsExportColumn.comments },
	{ label: "Shares", value: CampaignMetricsExportColumn.shares },
	{ label: "Post Status", value: CampaignMetricsExportColumn.postStatus },
	{ label: "Post ID", value: CampaignMetricsExportColumn.postId },
	{ label: "Post Draft Link", value: CampaignMetricsExportColumn.draftUrl },
	{ label: "Campaign Name", value: CampaignMetricsExportColumn.campaignName },
	{
		label: "Metrics Hourly Breakdown",
		value: CampaignMetricsExportColumn.hourlyBreakdown,
	},
];

const DEFAULT_STATUSES = [PostStatus.postApproved];

const DEFAULT_COLUMNS = [
	CampaignMetricsExportColumn.pageName,
	CampaignMetricsExportColumn.profileLink,
	CampaignMetricsExportColumn.postLink,
	CampaignMetricsExportColumn.dateOfPost,
	CampaignMetricsExportColumn.views,
	CampaignMetricsExportColumn.likes,
	CampaignMetricsExportColumn.comments,
	CampaignMetricsExportColumn.shares,
];

export const ExportCampaignMetricsModalPartial: React.FunctionComponent =
	() => {
		const { execute: getCampaignByIdHttp, fetching: fetchingCampaign } =
			useGetCampaignByIdAdminHttp();
		const { execute: exportCsvBrandHttp, fetching: fetchingCsv } =
			useExportCsvCampaignMetricsAdminHttp();

		const [campaignData, setCampaignData] = useState<ICampaignMinimal>();
		const [selectedStatuses, setSelectedStatuses] =
			useState<PostStatus[]>(DEFAULT_STATUSES);
		const [selectedColumns, setSelectedColumns] =
			useState<CampaignMetricsExportColumn[]>(DEFAULT_COLUMNS);

		const { toast } = useToast();
		const { campaignId } = useParams();

		const allStatusesSelected =
			selectedStatuses.length === STATUS_OPTIONS.length;
		const allColumnsSelected = selectedColumns.length === COLUMN_OPTIONS.length;

		function toggleStatus(status: PostStatus) {
			setSelectedStatuses((current) => {
				if (current.includes(status)) {
					if (current.length === 1) {
						return current;
					}
					return current.filter((item) => item !== status);
				}
				return [...current, status];
			});
		}

		function toggleColumn(column: CampaignMetricsExportColumn) {
			setSelectedColumns((current) =>
				current.includes(column)
					? current.filter((item) => item !== column)
					: [...current, column],
			);
		}

		function toggleAllStatuses() {
			setSelectedStatuses(
				allStatusesSelected
					? DEFAULT_STATUSES
					: STATUS_OPTIONS.map((option) => option.value),
			);
		}

		function toggleAllColumns() {
			setSelectedColumns(
				allColumnsSelected ? [] : COLUMN_OPTIONS.map((option) => option.value),
			);
		}

		async function ExportCsv() {
			if (!campaignId || selectedColumns.length === 0) {
				return;
			}
			const response = await exportCsvBrandHttp({
				body: null,
				pathParams: { id: campaignId },
				queryParams: {
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					statuses: selectedStatuses.join(","),
					columns: selectedColumns.join(","),
				},
			});
			if (!response.success) {
				return handleHttpError(response, toast);
			}
			window.open(response.data.url, "_blank");
		}

		async function getCampaignById() {
			if (!campaignId) {
				return;
			}
			const response = await getCampaignByIdHttp({
				body: null,
				pathParams: { id: campaignId },
			});
			if (!response.success) {
				return handleHttpError(response, toast);
			}
			setCampaignData(response.data.campaign);
		}

		// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
		useEffect(() => {
			getCampaignById();
		}, []);

		return (
			<DialogContent className="flex flex-col gap-0 border-border p-0 w-full max-h-[85vh] text-primary">
				<div className="flex flex-col gap-5 p-6 pb-4">
					<DialogHeader className="w-full">
						<div className="flex flex-col gap-2.5">
							<p className="font-semibold text-28 leading-8">
								Export campaign analytics
							</p>
							<p className="flex items-center gap-1 text-secondary">
								<span>Campaign: </span>
								{fetchingCampaign ? <Spinner width={20} /> : campaignData?.name}
							</p>
						</div>
					</DialogHeader>
				</div>
				<div className="border-b border-border w-full" />
				<div className="flex-1 p-6 overflow-y-auto">
					<div className="gap-6 grid grid-cols-1 md:grid-cols-2">
						<div className="flex flex-col gap-4">
							<div className="flex justify-between items-center gap-3">
								<p className="font-semibold text-lg">Post status</p>
								<label className="flex items-center gap-2 text-secondary text-sm cursor-pointer">
									<Checkbox
										checked={allStatusesSelected}
										onCheckedChange={toggleAllStatuses}
									/>
									Select all
								</label>
							</div>
							<div className="gap-3 grid grid-cols-1">
								{STATUS_OPTIONS.map((status) => (
									<label
										key={status.value}
										className="flex items-center gap-3 bg-bginput px-4 py-3 border border-border rounded-lg cursor-pointer"
									>
										<Checkbox
											checked={selectedStatuses.includes(status.value)}
											onCheckedChange={() => toggleStatus(status.value)}
										/>
										<span className="font-semibold text-sm">
											{status.label}
										</span>
									</label>
								))}
							</div>
						</div>
						<div className="flex flex-col gap-4">
							<div className="flex justify-between items-center gap-3">
								<p className="font-semibold text-lg">Columns</p>
								<label className="flex items-center gap-2 text-secondary text-sm cursor-pointer">
									<Checkbox
										checked={allColumnsSelected}
										onCheckedChange={toggleAllColumns}
									/>
									Select all
								</label>
							</div>
							<div className="gap-3 grid grid-cols-1">
								{COLUMN_OPTIONS.map((column) => (
									<label
										key={column.value}
										className="flex items-center gap-3 bg-bginput px-4 py-3 border border-border rounded-lg cursor-pointer"
									>
										<Checkbox
											checked={selectedColumns.includes(column.value)}
											onCheckedChange={() => toggleColumn(column.value)}
										/>
										<span className="font-semibold text-sm">
											{column.label}
										</span>
									</label>
								))}
							</div>
						</div>
					</div>
				</div>
				<div className="flex justify-end border-border border-t p-6">
					<Button
						disabled={selectedColumns.length === 0 || fetchingCsv}
						onClick={ExportCsv}
						className="bg-primarybuttonbg w-48 py-6 font-semibold text-base text-black"
					>
						{fetchingCsv ? <Spinner width={20} /> : "Download CSV"}
					</Button>
				</div>
			</DialogContent>
		);
	};
